import React, { useState, useRef, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Mic, Play, Pause, Square, Trash2, Volume2, Circle, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { detectDominantNote, semitoneOffset, playbackRateForSemitones, NOTE_LIST } from '../lib/pitch';

/**
 * Loop Station — 5 pads that record from the mic and loop.
 * Behaviour modeled on the Boss RC-505:
 *  • Empty → hold RECORD to arm; recording starts on first sound (auto-start disabled here; starts on click)
 *  • Recording → press RECORD/STOP to close loop and play it
 *  • Playing → press again to stop; hold CLEAR to erase
 *  • Multiple tracks play simultaneously, in sync (all started from the master play).
 */

const NUM_PADS = 5;

function useMicRecorder() {
  const streamRef = useRef(null);

  const getStream = useCallback(async () => {
    if (streamRef.current) return streamRef.current;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    return stream;
  }, []);

  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
  }, []);

  return { getStream };
}

export const LoopStation = () => {
  const { getStream } = useMicRecorder();
  const audioCtxRef = useRef(null);
  const [pads, setPads] = useState(
    Array.from({ length: NUM_PADS }, () => ({
      state: 'empty',   // 'empty' | 'recording' | 'stopped' | 'playing'
      blobUrl: null,
      volume: 1,
    }))
  );
  const recordersRef = useRef({});   // idx -> MediaRecorder
  const audioElsRef = useRef({});    // idx -> HTMLAudioElement
  const [masterVol, setMasterVol] = useState(0.9);
  const [masterKey, setMasterKey] = useState('');  // '' = key sync off
  const [keySync, setKeySync] = useState(true);
  const [deckNudge, setDeckNudge] = useState(null); // {deck, key, bpm, title} pre-warmed from Decks

  // Auto-Loop-Match: when a track loads on a deck, mirror its key into masterKey
  // so any triggered loop pad key-locks instantly without the DJ picking again.
  useEffect(() => {
    const apply = (payload) => {
      if (!payload || !payload.key) return;
      const tonic = payload.key.split(/\s+/)[0];
      setDeckNudge(payload);
      setMasterKey(tonic);
    };
    // Hydrate from last known deck payload on mount
    try {
      const raw = localStorage.getItem('rawmixes.deck.current');
      if (raw) apply(JSON.parse(raw));
    } catch {}
    // React to live deck loads while the Loop Station is open
    const onDeckLoaded = (e) => apply(e.detail);
    const onStorage = (e) => {
      if (e.key === 'rawmixes.deck.current' && e.newValue) {
        try { apply(JSON.parse(e.newValue)); } catch {}
      }
    };
    window.addEventListener('rawmixes:deck-loaded', onDeckLoaded);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('rawmixes:deck-loaded', onDeckLoaded);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const applyKeyRate = useCallback((idx, pad) => {
    const el = audioElsRef.current[idx];
    if (!el) return;
    if (!keySync || !masterKey || !pad?.detectedNote) {
      el.playbackRate = 1;
      return;
    }
    // Note: playbackRate shifts pitch AND tempo together (tape-varispeed).
    // Fine for musical loops within ±6 semitones; DJs can un-sync any pad.
    el.preservesPitch = false;
    const offset = semitoneOffset(pad.detectedNote, masterKey);
    el.playbackRate = playbackRateForSemitones(offset);
  }, [keySync, masterKey]);

  // Re-apply key rate to every playing pad whenever master key / sync toggles
  useEffect(() => {
    pads.forEach((pad, idx) => applyKeyRate(idx, pad));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterKey, keySync]);

  const getCtx = () => {
    if (!audioCtxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      audioCtxRef.current = new AC();
    }
    if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
    return audioCtxRef.current;
  };

  useEffect(() => () => { try { audioCtxRef.current?.close(); } catch {} }, []);

  const updatePad = (idx, patch) => {
    setPads(p => p.map((pad, i) => (i === idx ? { ...pad, ...patch } : pad)));
  };

  const startRecord = async (idx) => {
    try {
      const stream = await getStream();
      getCtx(); // ensure audio context is unlocked
      const chunks = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(chunks, { type: mr.mimeType || 'audio/webm' });
        const url = URL.createObjectURL(blob);
        // Prepare audio element
        const el = new Audio(url);
        el.loop = true;
        el.volume = pads[idx]?.volume ?? 1;
        audioElsRef.current[idx] = el;
        // Detect the dominant note client-side so we can key-sync this pad
        const detected = await detectDominantNote(blob);
        updatePad(idx, {
          state: 'stopped',
          blobUrl: url,
          detectedNote: detected?.note || null,
          detectedFreq: detected?.freq || null,
        });
        if (detected?.note) toast.success(`Pad ${idx + 1}: detected ${detected.note}`);
      };
      recordersRef.current[idx] = mr;
      mr.start();
      updatePad(idx, { state: 'recording' });
      toast.success(`Recording pad ${idx + 1}`);
    } catch (err) {
      toast.error('Microphone permission denied. Enable it in your browser to loop.');
    }
  };

  const stopRecord = (idx) => {
    const mr = recordersRef.current[idx];
    if (mr && mr.state !== 'inactive') mr.stop();
  };

  const play = (idx) => {
    const el = audioElsRef.current[idx];
    if (!el) return;
    applyKeyRate(idx, pads[idx]);
    el.currentTime = 0;
    el.play().then(() => updatePad(idx, { state: 'playing' })).catch(() => {});
  };

  const stopPlay = (idx) => {
    const el = audioElsRef.current[idx];
    if (!el) return;
    el.pause();
    el.currentTime = 0;
    updatePad(idx, { state: 'stopped' });
  };

  const clear = (idx) => {
    // Stop the recorder if still recording (fixes mic-leak on clear-mid-record)
    const mr = recordersRef.current[idx];
    if (mr && mr.state !== 'inactive') {
      try { mr.stop(); } catch {}
    }
    const el = audioElsRef.current[idx];
    if (el) { el.pause(); URL.revokeObjectURL(el.src); }
    delete audioElsRef.current[idx];
    delete recordersRef.current[idx];
    updatePad(idx, { state: 'empty', blobUrl: null });
  };

  const setPadVolume = (idx, v) => {
    updatePad(idx, { volume: v });
    const el = audioElsRef.current[idx];
    if (el) el.volume = v * masterVol;
  };

  useEffect(() => {
    // Update all playing pads with master volume
    pads.forEach((pad, idx) => {
      const el = audioElsRef.current[idx];
      if (el) el.volume = pad.volume * masterVol;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterVol]);

  const playAll = () => {
    pads.forEach((pad, idx) => {
      const el = audioElsRef.current[idx];
      if (el && pad.state !== 'playing') {
        applyKeyRate(idx, pad);
        el.currentTime = 0;
        el.play();
        updatePad(idx, { state: 'playing' });
      }
    });
  };

  const stopAll = () => {
    pads.forEach((pad, idx) => {
      const el = audioElsRef.current[idx];
      if (el && pad.state === 'playing') {
        el.pause();
        el.currentTime = 0;
        updatePad(idx, { state: 'stopped' });
      }
    });
  };

  const clearAll = () => {
    pads.forEach((_, idx) => clear(idx));
  };

  const anyPlaying = pads.some(p => p.state === 'playing');

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFB000] mb-2">Loop Station</div>
        <h1 className="font-heading font-black text-white text-4xl md:text-5xl tracking-tighter">
          Layer your sound.
        </h1>
        <p className="text-[#A1A1AA] text-sm mt-2 max-w-lg">
          Tap a pad to record from your mic. Tap again to close the loop. Layer up to 5 loops and jam.
        </p>
      </div>

      {/* Master controls */}
      <div className="post-card p-5 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={playAll}
              className="btn-pill btn-primary text-xs flex items-center gap-2"
              data-testid="master-play-all"
            >
              <Play size={14} /> Play all
            </button>
            <button
              onClick={stopAll}
              disabled={!anyPlaying}
              className="btn-pill btn-outline text-xs flex items-center gap-2 disabled:opacity-40"
              data-testid="master-stop-all"
            >
              <Square size={14} /> Stop all
            </button>
            <button
              onClick={clearAll}
              className="btn-pill btn-outline text-xs flex items-center gap-2 hover:border-[#EF4444]/50 hover:text-[#EF4444]"
              data-testid="master-clear-all"
            >
              <Trash2 size={14} /> Clear
            </button>
          </div>
          <div className="flex items-center gap-2 min-w-[180px]">
            <Volume2 size={14} className="text-[#A1A1AA]" />
            <input
              type="range" min="0" max="1" step="0.01" value={masterVol}
              onChange={e => setMasterVol(parseFloat(e.target.value))}
              className="flex-1 accent-[#FFB000]"
              data-testid="loop-master-vol"
              aria-label="Master volume"
            />
          </div>
        </div>

        {/* Key sync row */}
        <div className="mt-4 pt-4 border-t border-white/5 flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setKeySync(v => !v)}
            className={`btn-pill text-xs flex items-center gap-1.5 ${keySync ? 'btn-primary' : 'btn-outline'}`}
            data-testid="loop-key-sync-toggle"
            title="Auto pitch-shift every loop to match the master key"
          >
            <Zap size={12} /> Key sync {keySync ? 'ON' : 'OFF'}
          </button>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold">Master key</div>
          <select
            value={masterKey}
            onChange={e => setMasterKey(e.target.value)}
            className="input-default !py-1.5 !w-auto text-xs"
            data-testid="loop-master-key-select"
          >
            <option value="">— free —</option>
            {NOTE_LIST.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          {masterKey && keySync && (
            <span className="text-[10px] text-[#22C55E] font-bold uppercase tracking-widest" data-testid="loop-key-sync-active">
              Auto-tuning to {masterKey}
              {deckNudge && deckNudge.key && deckNudge.key.startsWith(masterKey) && (
                <span className="ml-2 text-[#FFB000] normal-case tracking-normal">· from Deck {deckNudge.deck}</span>
              )}
            </span>
          )}
          {!keySync && (
            <span className="text-[10px] text-[#A1A1AA]">Loops play in their original key.</span>
          )}
        </div>
      </div>

      {/* Pads */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {pads.map((pad, idx) => {
          const busy = pad.state === 'recording' || pad.state === 'playing';
          const ringColor =
            pad.state === 'recording' ? 'text-[#EF4444]'
            : pad.state === 'playing' ? 'text-[#22C55E]'
            : pad.state === 'stopped' ? 'text-[#FFB000]'
            : 'text-white/20';
          return (
            <div key={idx} className="post-card p-4 flex flex-col items-center gap-3" data-testid={`pad-${idx + 1}`}>
              <div className="flex items-center justify-between w-full">
                <div className="text-2xl font-heading font-black text-[#FFB000]">{idx + 1}</div>
                {pad.detectedNote && (
                  <div className="text-[10px] font-mono font-bold text-[#FFB000] bg-[#FFB000]/10 border border-[#FFB000]/30 px-1.5 py-0.5 rounded" data-testid={`pad-${idx + 1}-note`}>
                    {pad.detectedNote}
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  if (pad.state === 'empty') startRecord(idx);
                  else if (pad.state === 'recording') stopRecord(idx);
                  else if (pad.state === 'stopped') play(idx);
                  else if (pad.state === 'playing') stopPlay(idx);
                }}
                className={`relative w-24 h-24 rounded-full border-4 flex items-center justify-center transition-colors ${
                  pad.state === 'recording' ? 'border-[#EF4444] bg-[#EF4444]/10 shadow-[0_0_30px_rgba(239,68,68,0.4)]'
                  : pad.state === 'playing' ? 'border-[#22C55E] bg-[#22C55E]/10 shadow-[0_0_30px_rgba(34,197,94,0.4)]'
                  : pad.state === 'stopped' ? 'border-[#FFB000] bg-[#FFB000]/5 hover:bg-[#FFB000]/15'
                  : 'border-white/15 bg-[#1A1A1D] hover:border-[#FFB000]/40'
                }`}
                data-testid={`pad-${idx + 1}-action`}
                aria-label={
                  pad.state === 'empty' ? 'Record' :
                  pad.state === 'recording' ? 'Stop recording' :
                  pad.state === 'playing' ? 'Stop playback' : 'Play'
                }
              >
                {/* Inner pulse overlay — animates without moving the click target */}
                {busy && (
                  <motion.span
                    aria-hidden
                    className={`absolute inset-0 rounded-full pointer-events-none ${
                      pad.state === 'recording' ? 'shadow-[0_0_40px_rgba(239,68,68,0.6)]'
                      : 'shadow-[0_0_40px_rgba(34,197,94,0.6)]'
                    }`}
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                  />
                )}
                {pad.state === 'empty' && <Mic size={30} className="text-[#A1A1AA]" />}
                {pad.state === 'recording' && <Circle size={30} fill="#EF4444" stroke="none" />}
                {pad.state === 'stopped' && <Play size={30} className="text-[#FFB000] ml-1" fill="currentColor" />}
                {pad.state === 'playing' && <Pause size={30} className="text-[#22C55E]" fill="currentColor" />}
              </button>
              <div className={`text-[10px] uppercase tracking-widest font-bold ${ringColor}`}>
                {pad.state === 'empty' && 'READY'}
                {pad.state === 'recording' && 'RECORDING'}
                {pad.state === 'stopped' && 'LOOPED'}
                {pad.state === 'playing' && 'PLAYING'}
              </div>
              {pad.state !== 'empty' && pad.state !== 'recording' && (
                <>
                  <input
                    type="range" min="0" max="1" step="0.01" value={pad.volume}
                    onChange={e => setPadVolume(idx, parseFloat(e.target.value))}
                    className="w-full accent-[#FFB000]"
                    data-testid={`pad-${idx + 1}-vol`}
                    aria-label={`Pad ${idx + 1} volume`}
                  />
                  <button
                    onClick={() => clear(idx)}
                    className="text-[10px] uppercase tracking-widest text-[#A1A1AA] hover:text-[#EF4444] transition-colors font-bold"
                    data-testid={`pad-${idx + 1}-clear`}
                  >
                    <Trash2 size={12} className="inline mr-1" />
                    Clear
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-[#52525B] text-xs mt-8">
        Tip: allow microphone access when prompted. All loops stay in your browser and are cleared when you refresh.
      </p>
    </Layout>
  );
};

export default LoopStation;
